import React, { useState, useEffect } from 'react';
import { tenantDoctorAPI } from '../../../services/api/tenantDoctorAPI';
import { useAuth } from '../../../services/contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  UserCheck, 
  Heart, 
  Activity, 
  FileText, 
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Brain,
  Stethoscope,
  Award,
  Users
} from 'lucide-react';

const TenantDoctorDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    total_patients: 0,
    pending_reviews: 0,
    critical_alerts: 0,
    reviews_completed_today: 0,
    doctor_name: ''
  });
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [summaryResponse, patientsResponse] = await Promise.all([
        tenantDoctorAPI.getDashboardSummary(),
        tenantDoctorAPI.getPatients()
      ]);
      
      setDashboardData(summaryResponse.data);
      setRecentPatients(patientsResponse.data.slice(0, 5)); // Show only first 5 patients
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = "blue", alert = false, trend = null }) => (
    <div className={`bg-white p-6 rounded-xl shadow-sm border-l-4 ${
      alert ? 'border-l-[#207CE5]' : `border-l-[#207CE5]`
    } border-r border-t border-b border-[#DCEAF5]`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-[#3D3D3D]">{title}</p>
          <p className={`text-2xl font-bold mt-2 ${
            alert ? 'text-[#207CE5]' : `text-[#207CE5]`
          }`}>{value}</p>
          {trend && (
            <p className="text-xs text-[#3D3D3D] mt-1">{trend}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${
          alert ? 'bg-[#F9FBFC] border border-[#207CE5]' : `bg-[#DCEAF5]`
        }`}>
          <Icon className={`h-6 w-6 ${
            alert ? 'text-[#207CE5]' : `text-[#207CE5]`
          }`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
      <div>
          <p className="text-black font-bold text-lg">Welcome back,</p>
          <p className="text-[#3D3D3D] text-xl">{user?.name}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-[#3D3D3D]">Clinical Session</p>
          <p className="text-lg font-semibold text-[#207CE5]">Active</p>
        </div>
      </div>

      {/* Key Clinical Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Active Patients"
          value={dashboardData.total_patients}
          icon={UserCheck}
          color="blue"
        />
        <StatCard
          title="Pending Reviews"
          value={dashboardData.pending_reviews}
          icon={Clock}
          color="amber"
          trend="4 urgent, 8 routine"
        />
        <StatCard
          title="Critical Alerts"
          value={dashboardData.critical_alerts}
          icon={AlertTriangle}
          alert={dashboardData.critical_alerts > 0}
          trend="Irregular rhythms detected"
        />
        <StatCard
          title="Reviews Completed"
          value={dashboardData.reviews_completed_today}
          icon={CheckCircle}
          color="green"
          trend="Today's progress"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Clinical Actions & Insights */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-[#DCEAF5]">
            <div className="p-6 border-b border-[#DCEAF5]">
              <h3 className="text-lg font-semibold text-[#0B1C2C]">Alerts</h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {recentPatients.filter(patient => 
                  patient.latest_vital && 
                  ['Irregular', 'Atrial Fibrillation'].includes(patient.latest_vital.rhythm)
                ).slice(0, 3).map((patient) => (
                  <div key={patient.id} className="flex items-center space-x-3 p-3 bg-[#F9FBFC] rounded-lg border border-[#207CE5] hover:bg-[#DCEAF5] transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#207CE5] to-[#4682B4] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {patient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0B1C2C] truncate">{patient.alias}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <AlertTriangle className="h-4 w-4 text-[#207CE5]" />
                        <span className="text-sm text-[#207CE5] font-medium">
                          {patient.latest_vital.rhythm}
                        </span>
                        <span className="text-xs text-[#3D3D3D]">
                          HR: {patient.latest_vital.heart_rate || 'N/A'}
                        </span>
                      </div>
                      <p className="text-xs text-[#3D3D3D] mt-1">
                        {new Date(patient.latest_vital.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button className="p-2 text-[#207CE5] hover:text-[#4682B4] transition-colors">
                      <Stethoscope className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {recentPatients.filter(patient => 
                  patient.latest_vital && 
                  ['Irregular', 'Atrial Fibrillation'].includes(patient.latest_vital.rhythm)
                ).length === 0 && (
                  <div className="text-center py-6">
                    <CheckCircle className="h-8 w-8 text-[#008080] mx-auto mb-2" />
                    <p className="text-sm text-[#3D3D3D]">No critical alerts</p>
                    <p className="text-xs text-[#5B7DAE]">All patients are stable</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clinical Performance (Insights) */}
          {/* <div className="bg-white rounded-xl shadow-sm border border-[#DCEAF5]">
            <div className="p-6 border-b border-[#DCEAF5]">
              <div className="flex items-center space-x-2">
                <Brain className="h-5 w-5 text-[#4682B4]" />
                <h3 className="text-lg font-semibold text-[#0B1C2C]">Clinical Insights</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-[#F9FBFC] rounded-lg border border-[#207CE5]">
                  <AlertTriangle className="h-5 w-5 text-[#207CE5]" />
                  <div>
                    <p className="font-medium text-[#207CE5]">High Priority</p>
                    <p className="text-sm text-[#0B1C2C]">Patient #1247 shows irregular rhythm patterns</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-[#F9FBFC] rounded-lg border border-[#00BCD4]">
                  <TrendingUp className="h-5 w-5 text-[#00BCD4]" />
                  <div>
                    <p className="font-medium text-[#00BCD4]">Trending Analysis</p>
                    <p className="text-sm text-[#0B1C2C]">Overall patient metrics improving by 12%</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-[#F9FBFC] rounded-lg border border-[#008080]">
                  <CheckCircle className="h-5 w-5 text-[#008080]" />
                  <div>
                    <p className="font-medium text-[#008080]">Quality Metrics</p>
                    <p className="text-sm text-[#0B1C2C]">98.7% diagnostic accuracy maintained</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Performance Summary */}
          {/* <div className="bg-white rounded-xl shadow-sm border border-[#DCEAF5]">
            <div className="p-6 border-b border-[#DCEAF5]">
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5 text-[#008080]" />
                <h3 className="text-lg font-semibold text-[#0B1C2C]">Clinical Performance</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-[#3D3D3D]">Reviews Today</span>
                  <span className="text-sm font-medium text-[#0B1C2C]">{dashboardData.reviews_completed_today}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-[#3D3D3D]">Average Review Time</span>
                  <span className="text-sm font-medium text-[#0B1C2C]">4.2 min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-[#3D3D3D]">Diagnostic Accuracy</span>
                  <span className="text-sm font-medium text-[#008080]">98.7%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-[#3D3D3D]">Patient Satisfaction</span>
                  <span className="text-sm font-medium text-[#207CE5]">4.9/5.0</span>
                </div>
              </div>
            </div>
          </div> */}
        </div>

        {/* Recent Patient Reviews */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-[#DCEAF5]">
          <div className="p-6 border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#0B1C2C]">Recent Patient Activity</h3>
              <button className="text-sm text-[#207CE5] hover:text-[#4682B4] font-medium">
                View All Patients →
              </button>
            </div>
          </div>
          <div className="p-6">
            {recentPatients.length === 0 ? (
              <div className="text-center py-8">
                <UserCheck className="h-12 w-12 text-[#3D3D3D] mx-auto mb-4" />
                <p className="text-[#3D3D3D]">No recent patient activity</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentPatients.map((patient) => (
                  <div key={patient.id} className="flex items-center justify-between p-4 bg-[#F9FBFC] rounded-lg border border-[#DCEAF5]">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#207CE5] to-[#4682B4] rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {patient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-[#0B1C2C]">{patient.alias}</p>
                        <p className="text-sm text-[#3D3D3D]">
                          {patient.latest_vital ? 
                            `Latest: ${new Date(patient.latest_vital.created_at).toLocaleDateString()}` :
                            'No recent vitals'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {patient.latest_vital && patient.latest_vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(patient.latest_vital.rhythm) && (
                        <AlertTriangle className="h-4 w-4 text-[#207CE5]" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantDoctorDashboard;